import styled, { css } from 'styled-components';
import { Button, breakpoints, typography } from '@bitsoex/react-design-system';
import { H2, Small } from '@legacy/moon/particles/Typography';
import { Table } from '@components';
import Input from '@legacy/moon/atoms/Input';

export const PricesStatusContainer = styled.div`
  cursor: pointer;
  display: grid;
  width: 100%;

  > div {
    grid-gap: unset;
  }
  > a {
    border-radius: 10px;
    transition: all 0.2s ease-in-out;
    :hover {
      box-shadow: 0px 3px 6px rgb(175 188 198 / 60%);
      transform: translateY(-2px);
      text-decoration: none;
    }
    :active {
      box-shadow: none;
      transform: translateY(0);
    }
    :focus {
      box-shadow: none;
      transform: translateY(0);
    }
    > div {
      grid-gap: unset;
    }
  }
`;

export const PricesStatusActions = styled.div`
  display: flex;
  justify-content: center;
`;

export const PricesStatusValue = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

export const PricesStatusHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 8px;

  img {
    align-self: center;
  }
`;

export const PricesStatusGraph = styled.div<{ $loading: boolean }>`
  margin: 0 -6px 0 -6px;
  height: 100px;
  cursor: pointer;
  svg {
    cursor: pointer;
  }
  opacity: ${props => (props.$loading ? 0.6 : 1)};

  @media (min-width: ${breakpoints.tabletLarge}) {
    height: 140px;
  }
`;

export const Title = styled(H2)`
  && {
    margin-bottom: 0;
    color: ${props => props.theme?.colors?.onSurfaceHigh};
    font-weight: 700;
    font-size: 20px;
  }
`;

export const Subtext = styled(Small)`
  && {
    margin-bottom: 0;
    color: ${props => props.theme?.colors?.onSurfaceLow};
    font-weight: 700;
    font-size: 16px;
  }
`;

export const Text = styled.p`
  ${typography.bodyBaseBold}
  color: ${props => props.theme.colors?.onSurfaceHigh};
  margin-bottom: 0;
`;

export const BuyButton = styled(Button)`
  padding-top: 7px;
  padding-bottom: 7px;
  width: 100% !important;
  max-width: 100% !important;
  height: min-content;
`;

export const PricesTableContainer = styled.div`
  display: grid;
  row-gap: 16px;
  border: unset;
  border-radius: unset;
  padding: unset;
  @media (min-width: ${breakpoints.tabletSmall}) {
    border: solid 1px ${props => props.theme?.colors?.borderDefault};
    border-radius: 10px;
    padding: 16px;
  }
`;

export const PricesTableFilters = styled.div`
  min-width: 100%;

  display: flex;
  gap: 16px;
  flex-direction: column-reverse;
  justify-content: space-between;
  margin: 0 -24px;

  > div {
    overflow-x: auto;
    justify-content: flex-start;
    width: 100%;

    padding: 5px 29px 14px 29px;
    margin: -5px -5px -14px 0;
  }

  @media (min-width: ${breakpoints.tabletSmall}) {
    min-width: unset;
    flex-direction: column-reverse;
    align-items: flex-start;
    margin: 0;

    > div {
      overflow-x: unset;
      justify-content: unset;
      width: unset;

      padding: 0;
      margin: 0;
    }
  }
  @media (min-width: ${breakpoints.tabletLarge}) {
    gap: 0px;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    > div {
      justify-content: unset;
    }
  }
`;

export const PricesTableDescription = styled.div`
  background-color: ${props => props.theme.colors?.backgroundDefault};
  border-top: solid 1px ${props => props.theme.colors?.borderDefault};
  border-bottom: solid 1px ${props => props.theme.colors?.borderDefault};

  > p {
    line-height: 20px;
  }
  padding: 12px 24px;
  margin: 0 -24px;

  @media (min-width: ${breakpoints.tabletSmall}) {
    border-top: solid 1px ${props => props.theme.colors?.borderDefault};
    border-bottom: solid 1px ${props => props.theme.colors?.borderDefault};
    padding: 16px 42px;
    margin: 0 -16px;
    > p {
      font-size: 14px;
      line-height: 24px;
    }
  }
`;

export const PricesTablePagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  align-items: center;
`;

export const PricesTableContent = styled.div`
  height: 1168px;
`;

export const StyledSearchBar = styled(Input)`
  && {
    margin: 0 0 0 0;
    > input {
      border-radius: 99999px;
      color: ${props => props.theme.colors?.onSurfaceLow};
    }

    @media (min-width: ${breakpoints.tabletSmall}) {
      > input {
        width: 100%;
      }
    }
    @media (min-width: ${breakpoints.desktopSmall}) {
      > input {
        width: 318px;
      }
    }

    > label {
      color: ${props => props.theme.colors?.onSurfaceLow};
      font-weight: 400;
    }
  }
`;

type ResponsiveTableHeaderProps = {
  size: keyof Pick<typeof breakpoints, 'tabletLarge' | 'tabletSmall' | 'desktopSmall'>;
};

export const ResponsiveTableHeader = styled(Table.Header)<ResponsiveTableHeaderProps>`
  display: none;
  @media (min-width: ${props => breakpoints[props.size]}) {
    display: table-cell;
  }
`;

type ResponsiveTableCellProps = {
  size: keyof Pick<typeof breakpoints, 'tabletLarge' | 'tabletSmall' | 'desktopSmall'>;
};
export const ResponsiveTableCell = styled(Table.Cell)<ResponsiveTableCellProps>`
  display: none;
  @media (min-width: ${props => breakpoints[props.size]}) {
    display: table-cell;
  }
`;

export const TableHeaderSelect = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  place-items: center;

  .fade-in {
    display: flex;
    flex-direction: column;
    gap: 2px;
    position: absolute;
    width: 80px;
    margin-top: -4px;
    margin-left: -30px;

    > div {
      margin: 0;
    }
    > div:first-child {
      min-width: 96px;
    }
    > div:last-child {
      max-width: unset;
      width: 106px;
    }
  }

  @media (min-width: ${breakpoints.tabletSmall}) {
    .fade-in {
      margin-left: unset;
      flex-direction: row;
      gap: 4px;
      width: 168px;
      margin-top: unset;
      margin-left: -76px;
    }
  }

  @media (min-width: ${breakpoints.tabletLarge}) {
    .fade-in {
      flex-direction: row;
      flex-wrap: wrap;
      width: 90px;
      margin-left: -14px;
      flex-direction: column-reverse;
      align-items: flex-end;
      margin-top: -4px;

      > div:last-child {
        max-width: 70px;
        width: 100%;
      }
    }
  }
  @media (min-width: ${breakpoints.desktopMedium}) {
    width: 80px;

    .fade-in {
      margin-left: 30px;
      flex-direction: row;
      gap: 12px;
      width: 178px;
      margin-top: unset;
      align-items: unset;
      flex-wrap: unset;

      > div:last-child {
        max-width: 70px;
        width: 100%;
      }
    }
  }
`;

export const TableCellName = styled.p`
  ${typography.bodyBaseBold}
  color: ${props => props.theme.colors?.onSurfaceHigh};
  margin: 0;
  align-self: end;
`;

export const TableCellSubname = styled.small`
  ${typography.tiny}
  color: ${props => props.theme.colors?.onSurfaceLow};
  margin: 0;

  align-self: end;
  padding-bottom: 2px;
`;

export const TableCellChanger = styled.div`
  min-height: 60px;
  width: min-content;
  display: grid;
  justify-items: center;
  align-items: center;
  gap: 4px;
`;

export const TableCellActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;

  a {
    pointer-events: auto;
  }
  a:hover {
    text-decoration: unset;
  }
`;

export const TableRowLink = styled(Table.Row).attrs<{
  disableHover?: boolean;
  disableClick?: boolean;
  as?: 'tr' | 'a';
}>(props => ({
  as: props.as,
}))<{ disableHover: boolean; disableClick?: boolean; as?: 'tr' | 'a' }>`
  && {
    ${props =>
      props.disableClick &&
      css`
        pointer-events: none;
      `}
    :hover {
      background-color: ${props => (props.disableHover ? 'transparent' : props.theme.colors?.surfaceHover)};
    }
  }
`;

export const Tagline = styled.div`
  ${typography.tiny};
  color: ${props => props.theme.colors?.onStatusInfoAlt};
  background-color: ${props => props.theme.colors?.statusInfoAlt};
  border-radius: 8px;
  padding: 0px 8px;
  width: fit-content;
  height: fit-content;
  && {
    line-height: 1.75;
  }
`;

export const MobileTagline = styled.div`
  ${typography.tiny};
  font-size: 10px !important;
  line-height: 1rem !important;
  color: ${props => props.theme.colors?.onStatusInfoAlt};
  background-color: ${props => props.theme.colors?.statusInfoAlt};
  border-radius: 8px;
  padding: 1px 8px;
  margin-top: 2px;
  align-self: center;
  white-space: nowrap;
`;

export const Row = styled.li`
  border: solid 1px ${props => props.theme?.colors?.borderDefault};
  border-radius: 10px;
  overflow: hidden;
`;

export const RowHeader = styled.div`
  width: 100%;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  align-items: flex-start;
  cursor: pointer;

  padding: 12px;
  @media (min-width: ${breakpoints.mobileLarge}) {
    padding: 12px 16px;
  }
`;

export const RowContent = styled.div<{ open: boolean }>`
  transition: border-color 0.35s ease;
  border-top: solid 1px ${props => (props.open ? props.theme.colors?.borderDefault : 'transparent')};

  padding: 12px;
  @media (min-width: ${breakpoints.mobileLarge}) {
    padding: 12px 16px;
  }
`;

export const RowHeaderName = styled.p`
  ${typography.bodyBaseBold}
  color: ${props => props.theme.colors?.onSurfaceHigh};
  margin: 0;
`;

export const RowHeaderSubname = styled.small`
  ${typography.tiny}
  color: ${props => props.theme.colors?.onSurfaceLow};
  margin: 0;
`;

export const RowHeaderLeft = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  > div:first-child {
    align-items: flex-start;
  }
`;

export const RowHeaderRight = styled.div`
  display: flex;
  gap: 10px;

  > div:first-child {
    align-items: flex-end;
  }

  @media (min-width: ${breakpoints.mobileLarge}) {
    gap: 12px;
  }
`;

export const RowContentMultiActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  button,
  a {
    width: 100%;
    max-width: 100%;
    text-decoration: none;
  }

  @media (min-width: ${breakpoints.mobileLarge}) {
    flex-direction: row-reverse;
    justify-content: flex-start;
    gap: 16px;
    button,
    a {
      width: unset;
      max-width: unset;
    }
  }
`;
export const RowContentSingleAction = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  button,
  a {
    width: 50%;
    max-width: 50%;
    text-decoration: none;
  }

  @media (min-width: ${breakpoints.mobileLarge}) {
    gap: 16px;
    button,
    a {
      width: unset;
      max-width: unset;
    }
  }
`;

export const Slot = styled.div`
  display: flex;
  align-items: baseline;
  padding-top: 2px;

  > div {
    display: flex;
  }
`;

export const Section = styled.div`
  display: flex;
  gap: 8px;
  justify-content: space-between;
`;

export const SectionLabel = styled.p`
  white-space: nowrap;
  margin: 0;
  ${typography.bodyBase}
  color: ${props => props.theme.colors?.onSurfaceMedium};
`;

export const SectionTitle = styled.p`
  margin: 0;
  ${typography.bodyBaseBold}
  color: ${props => props.theme.colors?.onSurfaceLow};
`;

export const PageTitle = styled.h1`
  ${typography.heading};
  color: ${props => props.theme.colors?.onSurfaceHigh};
  margin: 0;
  text-align: center;
`;

export const NotFoundContainer = styled.div`
  animation: fadeIn 0.4s;
  display: flex;
  gap: 16px;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-top: 8px;
  @media (min-width: ${breakpoints.tabletSmall}) {
    padding-top: 36px;
  }
`;
export const NotFoundImage = styled.div`
  max-width: 500px;
  @media (min-width: ${breakpoints.tabletSmall}) {
    margin: 0;
    max-width: 600px;
  }
  @media (min-width: ${breakpoints.desktopSmall}) {
    margin: 0;
    max-width: 750px;
  }
`;

export const LearnContainer = styled.div`
  max-width: 903px;
  margin: 0 auto;
  width: 100%;
`;
